/**
 * Tip 140: open outbound Hub links in a new browser tab and focus it.
 * In-app React Router paths stay in the SPA.
 * iOS Hub WKWebView has no tab and no back button: native Safari when the shell
 * supports it, otherwise the in-app browser (same header as attachment preview).
 */

import { openHubInAppBrowser } from "@/lib/hubInAppBrowser";
import { isHubIosShell, nativeOpenURL } from "@/lib/hubShell";

const NEW_TAB_FEATURES = "noopener,noreferrer";

export function resolveHref(raw: string, base: string = typeof location !== "undefined" ? location.href : "https://hub.atw.net/"): string {
  const href = (raw || "").trim();
  if (!href || href === "#") return "";
  try {
    return new URL(href, base).href;
  } catch {
    return "";
  }
}

/** SPA / in-app navigation — keep same tab. */
export function isAppInternalHref(raw: string, base?: string): boolean {
  const href = (raw || "").trim();
  if (!href) return true;
  if (href.startsWith("#")) return true;
  if (href.startsWith("?") || href.startsWith("./") || href.startsWith("../")) return true;
  if (href.startsWith("/") && !href.startsWith("//")) return true;
  if (/^(javascript|data|blob):/i.test(href)) return true;

  const abs = resolveHref(href, base);
  if (!abs) return true;
  try {
    const url = new URL(abs);
    if (url.protocol !== "http:" && url.protocol !== "https:") return true;
    if (typeof location === "undefined") return false;
    return false;
  } catch {
    return true;
  }
}

/** http(s) destinations (and protocol-relative) open in a new tab. */
export function shouldOpenInNewTab(raw: string, base?: string): boolean {
  const href = (raw || "").trim();
  if (!href || href.startsWith("#")) return false;
  if (/^(mailto|tel|sms):/i.test(href)) return false;
  if (/^(javascript|data|blob):/i.test(href)) return false;
  if (href.startsWith("/") && !href.startsWith("//")) return false;
  if (href.startsWith("?") || href.startsWith("./") || href.startsWith("../")) return false;

  if (href.startsWith("//") || /^https?:\/\//i.test(href)) return true;

  const abs = resolveHref(href, base);
  if (!abs) return false;
  try {
    const url = new URL(abs);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
}

/**
 * Open href in a new tab and focus it. Returns the Window ref when allowed.
 * Call only from a user gesture.
 */
export function openHrefInNewTab(raw: string): Window | null {
  const abs = resolveHref(raw);
  if (!abs || !shouldOpenInNewTab(raw)) return null;
  if (isHubIosShell()) {
    if (nativeOpenURL(abs)) return null;
    openHubInAppBrowser(abs);
    return null;
  }
  const win = window.open(abs, "_blank", NEW_TAB_FEATURES);
  try {
    win?.focus();
  } catch {
    /* some browsers block focus; tab still opens */
  }
  return win;
}

export function stampNewTabAttributes(anchor: HTMLAnchorElement): void {
  const href = anchor.getAttribute("href") || "";
  if (!shouldOpenInNewTab(href)) return;
  anchor.setAttribute("target", "_blank");
  const rel = (anchor.getAttribute("rel") || "").toLowerCase();
  const parts = new Set(rel.split(/\s+/).filter(Boolean));
  parts.add("noopener");
  parts.add("noreferrer");
  anchor.setAttribute("rel", Array.from(parts).join(" "));
}

/** Capture-phase handler for light DOM (and retargeted events). */
export function handlePossibleExternalLinkClick(event: Event): void {
  if (event.defaultPrevented) return;
  if (!(event instanceof MouseEvent)) return;
  if (event.button !== 0) return;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

  const path = typeof event.composedPath === "function" ? event.composedPath() : [];
  let anchor: HTMLAnchorElement | null = null;
  for (const node of path) {
    if (node instanceof HTMLAnchorElement) {
      anchor = node;
      break;
    }
  }
  if (!anchor) {
    const t = event.target;
    if (t instanceof Element) anchor = t.closest("a[href]");
  }
  if (!anchor) return;
  if (anchor.hasAttribute("download")) return;
  if (anchor.dataset.hubSameTab === "1") return;

  const href = anchor.getAttribute("href") || "";
  if (!shouldOpenInNewTab(href)) return;

  event.preventDefault();
  event.stopPropagation();
  stampNewTabAttributes(anchor);
  openHrefInNewTab(href);
}

export function installExternalLinkClickHandler(root: ParentNode = document): () => void {
  const listener = (event: Event) => handlePossibleExternalLinkClick(event);
  root.addEventListener("click", listener, true);
  return () => root.removeEventListener("click", listener, true);
}
